import { Column } from "data/table.data";
import { BankStatement, FI_CODE } from "models/bank.model";
import {
  CardBuy,
  MenuType,
  MultiAccountCodeAmount,
} from "models/business.model";
import { ExtraInvoice, TaxInvoice, TaxItem } from "models/tax.model";
import { bankAccountNoPipe } from "pipes/bank.pipe";
import { bizNumberPipe } from "pipes/business.pipe";
import {
  convertBusinessType,
  convertCurrency,
  convertDate,
} from "utils/converter";
import { ChipAppearance, renderChip, renderDateTime } from "utils/renderer";

type ReviewCardBuyColIdType =
  | keyof CardBuy
  | "seperator"
  | "accountName"
  | "newzenSent";
export type ReviewBankStatementColIdType =
  | keyof BankStatement
  | "seperator"
  | "accountName"
  | "dealType"
  | "amount"
  | "accNoSplit"
  | "newzenSent";
type ReviewTaxInvoiceColIdType =
  | keyof TaxInvoice
  | "tranType"
  | "taxType"
  | "itemsName"
  | "seperator"
  | "accountName"
  | "memo"
  | "newzenSent";

export interface ReviewCardBuyColumnData extends Column {
  id: ReviewCardBuyColIdType;
}
export interface ReviewBankStatementColumnData extends Column {
  id: ReviewBankStatementColIdType;
}

export interface ReviewTaxInvoiceColumnData extends Column {
  id: ReviewTaxInvoiceColIdType;
}

type ReviewExtraInvoiceColIdType =
  | keyof ExtraInvoice
  | "seperator"
  | "accountName"
  | "newzenSent";

export interface ReviewExtraInvoiceColumnData extends Column {
  id: ReviewExtraInvoiceColIdType;
}

const COL_COMMON_LABEL_DATA = {
  accountCode: "계정코드",
  accountName: "계정과목명",
  isVatDeduction: "공제여부",
  seperator: "|",
};

const COL_CARDBUY_LABEL_DATA = {
  ...COL_COMMON_LABEL_DATA,
  aprvDt: "거래일자",
  mrntTxprNm: "거래처명",
  bmanClNm: "구분",
  bcNm: "업종",
  tfbNm: "업태",
  accSplCft: "공급가액",
  accVaTxamt: "세액",
  totaTrsAmt: "합계금액",
  memo: "사용자메모",
  lastModifiedDate: "변경일",
  newzenSent: "전송상태", // 현재 세무사랑만 보여주는 상태라 임시로 전송상태로 표기
  uploadDateTime: "전송일시",
};

export const COL_BANKSTATEMENT_LABEL_DATA: Partial<
  Record<ReviewBankStatementColIdType, string>
> = {
  ...COL_COMMON_LABEL_DATA,
  trDtTm: "거래일시",
  dealType: "거래",
  bankCd: "은행분류",
  accNo: "계좌번호",
  accNoSplit: "계좌",
  amount: "금액",
  balance: "잔액",
  trTp: "구분",
  trNm: "적요",
  storeName: "거래처명",
  userMemo: "사용자메모",
  newzenSent: "전송상태", // 현재 세무사랑만 보여주는 상태라 임시로 전송상태로 표기
  uploadDateTime: "전송일시",
};

const COL_TAXINVOICE_LABEL_DATA = {
  ...COL_COMMON_LABEL_DATA,
  makeDt: "작성일자",
  tranType: "거래구분",
  taxType: "구분", // 과세구분
  corpNm: "상호",
  bizNo: "사업자번호",
  itemsName: "품목명",
  supAmt: "공급가액",
  taxAmt: "부가세",
  totAmt: "합계액",
  memo: "사용자메모",
  isPaper: "전자여부",
  newzenSent: "전송상태", // 현재 세무사랑만 보여주는 상태라 임시로 전송상태로 표기
  uploadDateTime: "전송일시",
};

export const REVIEW_CARDBUY_COLS: ReviewCardBuyColumnData[] = [
  {
    id: "aprvDt",
    label: COL_CARDBUY_LABEL_DATA.aprvDt,
    sorting: true,
    sx: { whiteSpace: "nowrap", fontSize: 14 },
  },
  {
    id: "mrntTxprNm",
    label: COL_CARDBUY_LABEL_DATA.mrntTxprNm,
    sorting: true,
    sx: { width: 80, wordBreak: "break-all", fontSize: 14, fontWeight: 500 },
  },
  {
    id: "bmanClNm",
    label: COL_CARDBUY_LABEL_DATA.bmanClNm,
    sorting: true,
    renderer: (data) => {
      const convertedType: "일반" | "면세" | "법인" | "간이" =
        convertBusinessType(data.bmanClNm);

      const appearance: ChipAppearance = {
        color: "primary",
        variant: "filled",
        shape: "rounded",
      };
      switch (convertedType) {
        case "일반":
          appearance.variant = "outlined";
          break;
        case "간이":
          appearance.color = "white";
          appearance.variant = "outlined";
          appearance.shape = "square";
          break;
        case "면세":
          appearance.color = "gray";
          break;
        case "법인":
          break;
      }

      return renderChip(convertedType, appearance);
    },
    sx: { width: 40, whiteSpace: "nowrap" },
  },
  {
    id: "bcNm",
    label: COL_CARDBUY_LABEL_DATA.bcNm,
    sorting: true,
    sx: { width: 88, wordBreak: "break-all", fontSize: 14 },
  },
  {
    id: "tfbNm",
    label: COL_CARDBUY_LABEL_DATA.tfbNm,
    sorting: true,
    sx: { width: 104, wordBreak: "break-all", fontSize: 14 },
  },
  // {
  //   id: "transactionType",
  //   label: "분개",
  //   sorting: true,
  // },
  {
    id: "accSplCft",
    label: COL_CARDBUY_LABEL_DATA.accSplCft,
    sorting: true,
    renderer: (data) =>
      convertCurrency(
        data.accSplCft || data.accSplCft === 0 ? data.accSplCft : data.splCft
      ),
    sx: { textAlign: "right" },
  },
  {
    id: "accVaTxamt",
    label: COL_CARDBUY_LABEL_DATA.accVaTxamt,
    sorting: true,
    renderer: (data) =>
      convertCurrency(
        data.accVaTxamt || data.accVaTxamt === 0
          ? data.accVaTxamt
          : data.vaTxamt
      ),
    sx: { textAlign: "right" },
  },
  {
    id: "totaTrsAmt",
    label: COL_CARDBUY_LABEL_DATA.totaTrsAmt,
    sorting: true,
    renderer: (data) => convertCurrency(data.totaTrsAmt),
    sx: { textAlign: "right" },
  },
  {
    id: "seperator",
    label: COL_CARDBUY_LABEL_DATA.seperator,
    renderer: (data) => "|",
    sx: { width: 4, textAlign: "center" },
  },
  {
    id: "isVatDeduction",
    label: COL_CARDBUY_LABEL_DATA.isVatDeduction,
    sorting: true,
    renderer: (data) =>
      data.isVatDeduction === true || data.isVatDeduction === false
        ? data.isVatDeduction
          ? "공제"
          : "불공제"
        : data.isVatDeduction
        ? data.isVatDeduction
        : "-",
    tooltip: (data) => data.aiReasoning || "",
  },
  {
    id: "accountCode",
    label: COL_CARDBUY_LABEL_DATA.accountCode,
    sorting: true,
    input: { submit: () => {} },
    tooltip: (data) => data.aiReasoning || "",
  },
  {
    id: "accountName",
    label: COL_CARDBUY_LABEL_DATA.accountName,
    sorting: true,
  },
  {
    id: "memo",
    label: COL_CARDBUY_LABEL_DATA.memo,
    sorting: true,
  },
  {
    id: "lastModifiedDate",
    label: COL_CARDBUY_LABEL_DATA.lastModifiedDate,
    sorting: true,
    renderer: (data) => convertDate(data.lastModifiedDate),
    sx: { whiteSpace: "nowrap", fontSize: 14 },
  },
  {
    id: "newzenSent",
    label: COL_CARDBUY_LABEL_DATA.newzenSent!,
    sorting: true,
    renderer: (data) => (data.newzenSent ? "전송" : "미전송"),
  },
  {
    id: "uploadDateTime",
    label: COL_CARDBUY_LABEL_DATA.uploadDateTime,
    sorting: true,
    renderer: (data) => renderDateTime(data.uploadDateTime),
    sx: { whiteSpace: "nowrap", textAlign: "right" },
  },
];

export const REVIEW_BANKSTATEMENT_COLS: ReviewBankStatementColumnData[] = [
  {
    id: "trDtTm",
    label: COL_BANKSTATEMENT_LABEL_DATA.trDtTm!,
    sorting: true,
    renderer: (data) => convertDate(data.trDtTm),
    sx: { whiteSpace: "nowrap", fontSize: 14 },
  },
  {
    id: "dealType",
    label: COL_BANKSTATEMENT_LABEL_DATA.dealType!,
    renderer: (data) =>
      renderChip(data.dealType, {
        color: "primary",
        variant: data.dealType === "입금" ? "outlined" : "filled",
        shape: "rounded",
      }),
    sorting: true,
  },
  {
    id: "bankCd",
    label: COL_BANKSTATEMENT_LABEL_DATA.bankCd!,
    sorting: true,
    renderer: (data) => FI_CODE[data.bankCd as keyof typeof FI_CODE] || "-",
    sx: { width: 60 },
  },
  {
    id: "accNoSplit",
    label: COL_BANKSTATEMENT_LABEL_DATA.accNoSplit!,
    sorting: true,
    tooltip: (data) => bankAccountNoPipe(data.accNo, data.bankCd) || "-",
  },
  {
    id: "amount",
    label: COL_BANKSTATEMENT_LABEL_DATA.amount!,
    sorting: true,
    renderer: (data) => convertCurrency(data.amount),
    sx: { textAlign: "right" },
  },
  {
    id: "balance",
    label: COL_BANKSTATEMENT_LABEL_DATA.balance!,
    sorting: true,
    renderer: (data) => convertCurrency(data.balance),
    sx: { textAlign: "right" },
  },
  {
    id: "trTp",
    label: COL_BANKSTATEMENT_LABEL_DATA.trTp!,
    sorting: true,
    sx: { width: 64, wordBreak: "break-all" },
  },
  {
    id: "trNm",
    label: COL_BANKSTATEMENT_LABEL_DATA.trNm!,
    sorting: true,
    sx: { width: 80, wordBreak: "break-all", fontSize: 14 },
  },
  {
    id: "storeName",
    label: COL_BANKSTATEMENT_LABEL_DATA.storeName!,
    sorting: true,
    sx: { width: 96, wordBreak: "break-all", fontSize: 14, fontWeight: 500 },
  },
  {
    id: "seperator",
    label: COL_BANKSTATEMENT_LABEL_DATA.seperator!,
    renderer: (data) => "|",
    sx: { width: 4, textAlign: "center" },
  },
  {
    id: "accountCode",
    label: COL_BANKSTATEMENT_LABEL_DATA.accountCode!,
    sorting: true,
    input: { submit: () => {} },
    tooltip: (data) => {
      if (data.multiAccountCodes && data.multiAccountCodes.length > 0) {
        let accNameStr = "";
        data.multiAccountCodes.forEach((acc: MultiAccountCodeAmount) => {
          if (accNameStr) accNameStr += ", ";
          accNameStr += acc.accountCode;
        });
        return accNameStr;
      } else return data.aiReasoning || "";
    },
  },
  {
    id: "accountName",
    label: COL_BANKSTATEMENT_LABEL_DATA.accountName!,
    sorting: true,
  },
  {
    id: "userMemo",
    label: COL_BANKSTATEMENT_LABEL_DATA.userMemo!,
    sorting: true,
    sx: { wordBreak: "break-all", fontSize: 14 },
  },
  {
    id: "newzenSent",
    label: COL_BANKSTATEMENT_LABEL_DATA.newzenSent!,
    sorting: true,
    renderer: (data) => (data.newzenSent ? "전송" : "미전송"),
  },
  {
    id: "uploadDateTime",
    label: COL_BANKSTATEMENT_LABEL_DATA.uploadDateTime!,
    sorting: true,
    renderer: (data) => renderDateTime(data.uploadDateTime),
    sx: { whiteSpace: "nowrap", textAlign: "right" },
  },
];

export const REVIEW_TAXINVOICE_COLS: ReviewTaxInvoiceColumnData[] = [
  {
    id: "makeDt",
    label: COL_TAXINVOICE_LABEL_DATA.makeDt,
    sorting: true,
    sx: { whiteSpace: "nowrap", fontSize: 14 },
  },
  {
    id: "tranType",
    label: COL_TAXINVOICE_LABEL_DATA.tranType,
    sorting: true,
  },
  {
    id: "taxType",
    label: COL_TAXINVOICE_LABEL_DATA.taxType,
    sorting: true,
    renderer: (data) => {
      const appearance: ChipAppearance = {
        color: "primary",
        variant: "filled",
        shape: "rounded",
      };

      switch (data.taxType) {
        case "과세":
          appearance.variant = "outlined";
          break;
        case "면세":
          appearance.variant = "filled";
          break;
        case "영세":
          appearance.color = "white";
          appearance.variant = "outlined";
          appearance.shape = "square";
          break;
      }

      return renderChip(data.taxType, appearance);
    },
  },
  {
    id: "corpNm",
    label: COL_TAXINVOICE_LABEL_DATA.corpNm,
    sorting: true,
    sx: { width: 88, wordBreak: "break-all", fontSize: 14, fontWeight: 500 },
  },
  {
    id: "bizNo",
    label: COL_TAXINVOICE_LABEL_DATA.bizNo,
    sorting: true,
    sx: { width: 104, whiteSpace: "nowrap", fontSize: 14 },
    renderer: (data) => bizNumberPipe(data.bizNo) || "-",
  },
  {
    id: "itemsName",
    label: COL_TAXINVOICE_LABEL_DATA.itemsName,
    sorting: true,
    sx: { width: 120, wordBreak: "break-all", fontSize: 14 },
  },
  {
    id: "supAmt",
    label: COL_TAXINVOICE_LABEL_DATA.supAmt,
    sorting: true,
    renderer: (data) => convertCurrency(data.supAmt),
  },
  {
    id: "taxAmt",
    label: COL_TAXINVOICE_LABEL_DATA.taxAmt,
    sorting: true,
    renderer: (data) => convertCurrency(data.taxAmt),
  },
  {
    id: "totAmt",
    label: COL_TAXINVOICE_LABEL_DATA.totAmt,
    sorting: true,
    renderer: (data) => convertCurrency(data.totAmt),
  },
  {
    id: "seperator",
    label: COL_TAXINVOICE_LABEL_DATA.seperator,
    renderer: (data) => "|",
    sx: { width: 4, textAlign: "center" },
  },
  {
    id: "isVatDeduction",
    label: COL_TAXINVOICE_LABEL_DATA.isVatDeduction,
    sorting: true,
    renderer: (data) =>
      data.isVatDeduction === true || data.isVatDeduction === false
        ? data.isVatDeduction
          ? "공제"
          : "불공제"
        : data.isVatDeduction
        ? data.isVatDeduction
        : "-",
    tooltip: (data) => data.aiReasoning || "",
  },
  {
    id: "accountCode",
    label: COL_TAXINVOICE_LABEL_DATA.accountCode,
    sorting: true,
    input: { submit: () => {} },
    tooltip: (data) => data.aiReasoning || "",
  },
  {
    id: "accountName",
    label: COL_TAXINVOICE_LABEL_DATA.accountName,
    sorting: true,
  },
  {
    id: "memo",
    label: COL_TAXINVOICE_LABEL_DATA.memo,
    sorting: true,
  },
  {
    id: "isPaper",
    label: COL_TAXINVOICE_LABEL_DATA.isPaper,
    sorting: true,
    renderer: (data) => (data.isPaper ? "X" : "O"),
    sx: { textAlign: "center" },
  },
  {
    id: "newzenSent",
    label: COL_TAXINVOICE_LABEL_DATA.newzenSent!,
    sorting: true,
    renderer: (data) => (data.newzenSent ? "전송" : "미전송"),
  },
  {
    id: "uploadDateTime",
    label: COL_TAXINVOICE_LABEL_DATA.uploadDateTime,
    sorting: true,
    renderer: (data) => renderDateTime(data.uploadDateTime),
    sx: { whiteSpace: "nowrap", textAlign: "right" },
  },
];

export const REVIEW_TAXINVOICE_BUY_COLS: ReviewTaxInvoiceColumnData[] =
  REVIEW_TAXINVOICE_COLS.filter((col) => col.id !== "tranType").map((col) => {
    if (col.id === "corpNm") {
      // 매입세금계산서만 툴팁 추가
      return {
        ...col,
        tooltip: (data) => {
          return `${data.bcNm || "-"} / ${data.tfbNm || "-"}`;
        },
      };
    }
    return col;
  });

export const REVIEW_TAXINVOICE_SALES_COLS: ReviewTaxInvoiceColumnData[] =
  REVIEW_TAXINVOICE_COLS.filter(
    (col) => col.id !== "tranType" && col.id !== "isVatDeduction"
  );

const COL_EXTRAINVOICE_LABEL_DATA = {
  ...COL_COMMON_LABEL_DATA,
  makeDt: "작성일자",
  vatType: "구분",
  corpNm: "상호",
  bizNo: "사업자번호",
  itemName: "품목명",
  supAmt: "공급가액",
  taxAmt: "부가세",
  totAmt: "합계액",
  source: "출처",
  memo: "사용자메모",
  newzenSent: "전송상태", // 현재 세무사랑만 보여주는 상태라 임시로 전송상태로 표기
  uploadDateTime: "전송일시",
};

// 기타매입/기타매출 부가세 유형 코드 (매출 11~24, 매입 51~62)
const EXTRA_INVOICE_VAT_TYPE_LABELS: Record<string, string> = {
  "11": "과세",
  "12": "영세",
  "13": "면세",
  "14": "건별",
  "15": "간이",
  "16": "수출",
  "17": "카과",
  "18": "카면",
  "19": "카영",
  "20": "면건",
  "21": "전자",
  "22": "현과",
  "23": "현면",
  "24": "현영",
  "51": "과세",
  "52": "영세",
  "53": "면세",
  "54": "불공",
  "55": "수입",
  "56": "금전",
  "57": "카과",
  "58": "카면",
  "59": "카영",
  "60": "면건",
  "61": "현과",
  "62": "현면",
};

// 색상 구분용 대분류 (과세율 기준): 과세/영세/면세
const EXTRA_INVOICE_VAT_TYPE_CATEGORY: Record<
  string,
  "과세" | "영세" | "면세"
> = {
  "11": "과세", // 과세
  "12": "영세", // 영세
  "13": "면세", // 면세
  "14": "과세", // 건별
  "15": "과세", // 간이
  "16": "영세", // 수출
  "17": "과세", // 카과
  "18": "면세", // 카면
  "19": "영세", // 카영
  "20": "면세", // 면건
  "21": "과세", // 전자
  "22": "과세", // 현과
  "23": "면세", // 현면
  "24": "영세", // 현영
  "51": "과세", // 과세
  "52": "영세", // 영세
  "53": "면세", // 면세
  "54": "과세", // 불공 (표준세율, 공제만 불가)
  "55": "과세", // 수입
  "56": "과세", // 금전(등록기영수증)
  "57": "과세", // 카과
  "58": "면세", // 카면
  "59": "영세", // 카영
  "60": "면세", // 면건
  "61": "과세", // 현과
  "62": "면세", // 현면
};

export const REVIEW_EXTRAINVOICE_COLS: ReviewExtraInvoiceColumnData[] = [
  {
    id: "makeDt",
    label: COL_EXTRAINVOICE_LABEL_DATA.makeDt,
    sorting: true,
    sx: { whiteSpace: "nowrap", fontSize: 14 },
  },
  {
    id: "vatType",
    label: COL_EXTRAINVOICE_LABEL_DATA.vatType,
    sorting: true,
    renderer: (data) => {
      if (!data.vatType) return "-";
      const label = EXTRA_INVOICE_VAT_TYPE_LABELS[data.vatType] || data.vatType;
      const category = EXTRA_INVOICE_VAT_TYPE_CATEGORY[data.vatType];

      const appearance: ChipAppearance = {
        color: "primary",
        variant: "filled",
        shape: "rounded",
      };

      switch (category) {
        case "과세":
          appearance.variant = "outlined";
          break;
        case "면세":
          appearance.variant = "filled";
          break;
        case "영세":
          appearance.color = "white";
          appearance.variant = "outlined";
          appearance.shape = "square";
          break;
      }

      return renderChip(label, appearance);
    },
  },
  {
    id: "corpNm",
    label: COL_EXTRAINVOICE_LABEL_DATA.corpNm,
    sorting: true,
    sx: { width: 88, wordBreak: "break-all", fontSize: 14, fontWeight: 500 },
  },
  {
    id: "bizNo",
    label: COL_EXTRAINVOICE_LABEL_DATA.bizNo,
    sorting: true,
    sx: { width: 104, whiteSpace: "nowrap", fontSize: 14 },
    renderer: (data) => bizNumberPipe(data.bizNo) || "-",
  },
  {
    id: "itemName",
    label: COL_EXTRAINVOICE_LABEL_DATA.itemName,
    sorting: true,
    sx: { width: 120, wordBreak: "break-all", fontSize: 14 },
  },
  {
    id: "supAmt",
    label: COL_EXTRAINVOICE_LABEL_DATA.supAmt,
    sorting: true,
    renderer: (data) => convertCurrency(data.supAmt),
  },
  {
    id: "taxAmt",
    label: COL_EXTRAINVOICE_LABEL_DATA.taxAmt,
    sorting: true,
    renderer: (data) => convertCurrency(data.taxAmt),
  },
  {
    id: "totAmt",
    label: COL_EXTRAINVOICE_LABEL_DATA.totAmt,
    sorting: true,
    renderer: (data) => convertCurrency(data.totAmt),
  },
  {
    id: "seperator",
    label: COL_EXTRAINVOICE_LABEL_DATA.seperator,
    renderer: (data) => "|",
    sx: { width: 4, textAlign: "center" },
  },
  {
    id: "isVatDeduction",
    label: COL_EXTRAINVOICE_LABEL_DATA.isVatDeduction,
    sorting: true,
    renderer: (data) =>
      data.isVatDeduction === true || data.isVatDeduction === false
        ? data.isVatDeduction
          ? "공제"
          : "불공제"
        : data.isVatDeduction
        ? data.isVatDeduction
        : "-",
  },
  {
    id: "accountCode",
    label: COL_EXTRAINVOICE_LABEL_DATA.accountCode,
    sorting: true,
    input: { submit: () => {} },
  },
  {
    id: "accountName",
    label: COL_EXTRAINVOICE_LABEL_DATA.accountName,
    sorting: true,
  },
  {
    id: "memo",
    label: COL_EXTRAINVOICE_LABEL_DATA.memo,
    sorting: true,
  },
  {
    id: "newzenSent",
    label: COL_EXTRAINVOICE_LABEL_DATA.newzenSent!,
    sorting: true,
    renderer: (data) => (data.newzenSent ? "전송" : "미전송"),
  },
  {
    id: "uploadDateTime",
    label: COL_EXTRAINVOICE_LABEL_DATA.uploadDateTime,
    sorting: true,
    renderer: (data) => renderDateTime(data.uploadDateTime),
    sx: { whiteSpace: "nowrap", textAlign: "right" },
  },
];

export const REVIEW_EXTRAINVOICE_BUY_COLS: ReviewExtraInvoiceColumnData[] =
  REVIEW_EXTRAINVOICE_COLS.map((col) => {
    if (col.id === "corpNm") {
      // 매입만 툴팁 추가 (기존 매입세금계산서 탭과 동일)
      return {
        ...col,
        tooltip: (data: ExtraInvoice) => {
          return `${data.bcNm || "-"} / ${data.tfbNm || "-"}`;
        },
      };
    }
    return col;
  });

export const REVIEW_EXTRAINVOICE_SALES_COLS: ReviewExtraInvoiceColumnData[] =
  REVIEW_EXTRAINVOICE_COLS.filter((col) => col.id !== "isVatDeduction").flatMap(
    (col): ReviewExtraInvoiceColumnData[] =>
      col.id === "accountName"
        ? [
            col,
            {
              id: "source",
              label: COL_EXTRAINVOICE_LABEL_DATA.source,
              sorting: true,
              sx: { width: 96, wordBreak: "break-all", fontSize: 14 },
            },
          ]
        : [col]
  );

export const REVIEW_COL_DATA: Record<
  MenuType,
  (
    | ReviewCardBuyColumnData
    | ReviewBankStatementColumnData
    | ReviewTaxInvoiceColumnData
    | ReviewExtraInvoiceColumnData
  )[]
> = {
  card: REVIEW_CARDBUY_COLS,
  bank: REVIEW_BANKSTATEMENT_COLS,
  "tax-buy": REVIEW_TAXINVOICE_BUY_COLS,
  "tax-sales": REVIEW_TAXINVOICE_SALES_COLS,
  "extra-buy": REVIEW_EXTRAINVOICE_BUY_COLS,
  "extra-sales": REVIEW_EXTRAINVOICE_SALES_COLS,
};
